import { useState } from 'react'
import { useGetSelfHealingTargetsQuery, useSaveSelfHealingTargetsMutation } from '../services/sessionMonitoringApi'
import { SelfHealingTargetsSchema, type SelfHealingTargets as Targets } from '../schemas/selfHealing'

function TargetChoice({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  const [other, setOther] = useState(![20, 100].includes(value))
  return <fieldset>
    <legend>{label}</legend>
    <div className="continuous-testing-setup__amounts">
      {[20, 100].map(amount => <button key={amount} type="button" aria-pressed={!other && value === amount} onClick={() => { setOther(false); onChange(amount) }}>{amount}</button>)}
      <button type="button" aria-pressed={other} onClick={() => setOther(true)}>Other</button>
    </div>
    {other && <label>Sessions per day<input type="number" required min="0" max="10000" step="1" value={Number.isNaN(value) ? '' : value} onChange={event => onChange(event.target.valueAsNumber)} /></label>}
  </fieldset>
}

function TargetsEditor({ projectId, initial, canEdit }: { projectId: string; initial: Targets; canEdit: boolean }) {
  const [targets, setTargets] = useState(initial)
  const [save, request] = useSaveSelfHealingTargetsMutation()
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  return <form className="self-healing__form" onSubmit={async event => {
    event.preventDefault(); setError(''); setMessage('')
    const parsed = SelfHealingTargetsSchema.safeParse(targets)
    if (!parsed.success) { setError('Enter whole numbers from 0 to 10,000.'); return }
    try {
      await save({ projectId, targets: parsed.data }).unwrap()
      setMessage('Daily targets saved.')
    } catch { setError('Could not save daily targets. Please try again.') }
  }}>
    <p>Daily targets apply across this project. Work is spread throughout the day as sessions arrive. Enter 0 to pause either activity.</p>
    <fieldset disabled={!canEdit || request.isLoading}>
      <TargetChoice label="Sessions reviewed each day" value={targets.reviews_per_day} onChange={value => setTargets({ ...targets, reviews_per_day: value })} />
      <p>Each reviewer run costs 0.2 credits. A session counts once toward the daily target even when multiple reviewers inspect it.</p>
      <TargetChoice label="Sessions analyzed each day (converted to journeys)" value={targets.analyses_per_day} onChange={value => setTargets({ ...targets, analyses_per_day: value })} />
      {canEdit && <button type="submit" className="testing-setup__primary">{request.isLoading ? 'Saving…' : 'Save daily targets'}</button>}
    </fieldset>
    {message && <p role="status">{message}</p>}
    {error && <p role="alert">{error}</p>}
  </form>
}

export default function SelfHealingTargets({ projectId }: { projectId: string }) {
  const query = useGetSelfHealingTargetsQuery(projectId)
  return <section>
    <h3>Daily session targets</h3>
    {query.isLoading && <p role="status">Loading daily targets…</p>}
    {query.isError && <p role="alert">Could not load daily targets. <button type="button" onClick={() => void query.refetch()}>Retry</button></p>}
    {query.currentData && <TargetsEditor key={projectId} projectId={projectId} initial={query.currentData.targets} canEdit={query.currentData.can_edit} />}
  </section>
}
